import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CardHeader,
  CardSection,
  DropdownSelect,
  Flex,
  FormField,
  Label,
  Spinner,
  Table,
} from '@zillow/constellation';
import { createDefaultPageData } from 'types/rest/IPageData';
import {
  eAdminAccountStatus,
  IAdminUserData,
  IAdminUsersQuery,
} from '../../../types/administration/userAdministration/IAdminUser';
import { useListPagedAdminUsersQuery } from '../../../API/administration/AdminUsersAPI';
import { ePermissions } from '../../../types/administration/userAdministration/ePermissions';
import { useQueryStringQuery } from '../../../pages/useQueryStringQuery';
import { useHasPermissions } from 'components/accessControl/hooks/useHasPermissions';
import { TableHeader } from './subComponents/TableHeader';
import { TableBody } from './subComponents/TableBody';
import { PaginationControls } from './subComponents/PaginationControls';
import { ResultsSummary } from './subComponents/ResultsSummary';

const defaultQuery: IAdminUsersQuery = {
  pageNumber: 1,
  pageSize: 25,
  sortField: 'fullName',
  sortOrder: 'asc',
  status: eAdminAccountStatus.Active,
};

// Status filter options for DropdownSelect
const statusFilterOptions = [
  { label: 'All', value: eAdminAccountStatus.All },
  { label: 'Active', value: eAdminAccountStatus.Active },
  { label: 'Deactivated', value: eAdminAccountStatus.Deactivated },
];

export const AdminUsersSearchTable: React.FC<{ 
  setUpdateAdminUser: (adminUser: IAdminUserData) => void;
  setUpdateUserProfile: (adminUser: IAdminUserData) => void;
}> = ({
  setUpdateAdminUser,
  setUpdateUserProfile,
}) => {
  const { queryObject, setQueryObject } = useQueryStringQuery('users', defaultQuery, [], {
    convertPagination: true,
    convertNumbers: true,
    convertBooleans: true,
  });

  // Local page and pageSize state for updater-style changes
  const [currentPage, setCurrentPage] = useState(queryObject.pageNumber ?? 1);
  const [pageSize, setPageSize] = useState(queryObject.pageSize ?? 25);

  // Keep API-querying state in sync
  useEffect(() => {
    setQueryObject({
      ...queryObject,
      pageNumber: currentPage,
      pageSize,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize]);

  const hasReadPermission = useHasPermissions([ePermissions.AuthIdentityRead]);
  const { data: adminUsers = createDefaultPageData(), isFetching } = useListPagedAdminUsersQuery(queryObject, {
    skip: !hasReadPermission,
  });

  const sortOrderMap: Record<string, 'asc' | 'desc' | undefined> = {
    ascending: 'asc',
    descending: 'desc',
    none: undefined,
  };

  // Handle sorting
  const handleSortChange = useCallback(
    ({ sortField }: { sortField: string }, sortDirection: string) => {
      setQueryObject({
        ...queryObject,
        sortField: sortField,
        sortOrder: sortOrderMap[sortDirection],
        pageNumber: 1,
      });
      setCurrentPage(1);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryObject, setQueryObject]
  );

  // Handle status filter change
  const handleStatusChange = useCallback(
    (nextValue: unknown) => {
      if (typeof nextValue === 'string') {
        const selectedOption = statusFilterOptions.find((option) => option.label === nextValue);
        if (selectedOption) {
          setQueryObject({
            ...queryObject,
            status: selectedOption.value,
            pageNumber: 1,
          });
          setCurrentPage(1);
        }
      }
    },
    [queryObject, setQueryObject]
  );

  // Updater-function for pagination
  const handlePageChange = useCallback((updater: (curr: number) => number) => {
    setCurrentPage((curr) => {
      const next = updater(curr);
      return next;
    });
  }, []);

  const handlePageSizeChange = useCallback(
    (updater: (curr: number) => number) => {
      const next = updater(pageSize);
      setPageSize(next);
      setCurrentPage(1);
    },
    [pageSize]
  );

  // Find current status option
  const currentStatusOption = useMemo(
    () => statusFilterOptions.find((option) => option.value === (queryObject.status ?? eAdminAccountStatus.Active)),
    [queryObject.status]
  );

  return (
    <>
      <CardHeader marginBottom={0}>
        <Flex display="flex" justifyContent="space-between" alignItems="center" gap={1}>
          <Flex display="flex" alignItems="center" gap={1}>
            Admin Users
          </Flex>
          <Flex display="flex" alignItems="center" gap={1}>
            <FormField
              label={<Label>Status</Label>}
              control={
                <DropdownSelect
                  options={statusFilterOptions.map((option) => option.label)}
                  value={currentStatusOption?.label}
                  onChange={handleStatusChange}
                />
              }
            />
          </Flex>
        </Flex>
      </CardHeader>
      <CardSection padding={0} margin={0} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {isFetching && <Spinner />}
        {!isFetching && (
          <>
            <Table.Sortable
              defaultSortDirections={['ascending', 'none', null, null, null]}
              onSortChange={({ nextSortDirection }, props) => handleSortChange(props.id, nextSortDirection)}
            >
              <Table appearance="zebra" aria-label="Admin Users Table" size="sm">
                <TableHeader />
                <TableBody 
                  adminUsers={adminUsers.content} 
                  setUpdateAdminUser={setUpdateAdminUser}
                  setUpdateUserProfile={setUpdateUserProfile}
                />
              </Table>
            </Table.Sortable>
            <PaginationControls
              currentPage={currentPage}
              pageSize={pageSize}
              totalElements={adminUsers.totalElements}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
            <ResultsSummary pageSize={pageSize} pageNumber={currentPage} totalElements={adminUsers.totalElements} />
          </>
        )}
      </CardSection>
    </>
  );
};
